/**
 * Zone Map Editor — admin draws polygonal sub-areas inside a pincode.
 * Uses vanilla Leaflet + leaflet-draw on top of OpenStreetMap tiles.
 *
 * Customers' (lat, lng) at checkout is then point-in-polygon tested against
 * the active zones for their pincode (backend ST_Contains).
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import {
  X,
  MapPin,
  Trash2,
  Loader2,
  Edit2,
  Save,
  Power,
  Plus,
  AlertCircle,
  CheckCircle,
  Search,
} from 'lucide-react';
import axios from 'axios';
import deliveryService from '../../services/deliveryService';

const API_URL = process.env.REACT_APP_API_URL || 'https://bookmyfresh.com/api';

// Backend-proxied Google Places autosuggest
async function searchPlaces(query) {
  if (!query || query.length < 4) return [];
  try {
    const { data } = await axios.get(`${API_URL}/maps/autosuggest`, { params: { q: query } });
    return (data?.suggestedLocations || []).map((loc) => ({
      name: loc.placeName || loc.placeAddress,
      address: loc.placeAddress || '',
      lat: loc.latitude ? parseFloat(loc.latitude) : null,
      lng: loc.longitude ? parseFloat(loc.longitude) : null,
      placeId: loc.eLoc,
    }));
  } catch {
    return [];
  }
}

// place_id -> coords fallback when autosuggest returns no lat/lng
async function resolvePlaceId(placeId) {
  try {
    const { data } = await axios.get(`${API_URL}/maps/place-detail`, { params: { placeId } });
    if (data?.lat && data?.lng) return { lat: data.lat, lng: data.lng };
  } catch { /* ignore */ }
  return null;
}

// Jaipur centroid — fallback when no zones exist yet
const JAIPUR_CENTER = [26.9124, 75.7873];

const ZoneMapEditor = ({ pincode, onClose }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const zoneLayersRef = useRef(new Map()); // zoneId -> L.Polygon

  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingPolygon, setPendingPolygon] = useState(null); // GeoJSON + tmp layer
  const [newZoneName, setNewZoneName] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [busyId, setBusyId] = useState(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  const showSuccess = (msg) => {
    setSuccess(msg);
    setError('');
    setTimeout(() => setSuccess(''), 2500);
  };

  const showError = (msg) => {
    setError(msg);
    setSuccess('');
  };

  // ── Map init ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current, {
      center: JAIPUR_CENTER,
      zoom: 12,
      zoomControl: false,
    });
    L.control.zoom({ position: 'bottomleft' }).addTo(map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    const drawControl = new L.Control.Draw({
      position: 'topright',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { color: '#548B2E', weight: 2, fillOpacity: 0.25 },
        },
        polyline: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false,
      },
      edit: false, // we do edit via per-zone "redraw" instead
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      const geojson = layer.toGeoJSON();
      // GeoJSON Feature -> we want the geometry only
      setPendingPolygon({ geometry: geojson.geometry, layer });
      layer.setStyle({ color: '#f59e0b', dashArray: '4 4', fillOpacity: 0.2 });
      drawnItems.addLayer(layer);
    });

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // ── Render existing zones onto the map ──────────────────────────────────
  const renderZones = useCallback((zoneList) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Clear existing zone layers
    zoneLayersRef.current.forEach((layer) => map.removeLayer(layer));
    zoneLayersRef.current.clear();

    const allBounds = [];
    zoneList.forEach((z) => {
      const geo = z.polygonGeojson;
      if (!geo || !geo.coordinates) return;
      // GeoJSON ring is [lng, lat]; Leaflet wants [lat, lng]
      const latlngs = geo.coordinates[0].map(([lng, lat]) => [lat, lng]);
      const polygon = L.polygon(latlngs, {
        color: z.isActive ? '#548B2E' : '#9ca3af',
        weight: 2,
        fillOpacity: z.isActive ? 0.2 : 0.1,
        dashArray: z.isActive ? null : '4 4',
      }).addTo(map);
      polygon.bindTooltip(`${z.name}${z.isActive ? '' : ' (inactive)'}`, { sticky: true });
      zoneLayersRef.current.set(z.id, polygon);
      allBounds.push(polygon.getBounds());
    });

    if (allBounds.length > 0) {
      const combined = allBounds.reduce((acc, b) => acc.extend(b), L.latLngBounds(allBounds[0].getSouthWest(), allBounds[0].getNorthEast()));
      map.fitBounds(combined, { padding: [40, 40] });
    } else {
      map.setView(JAIPUR_CENTER, 12);
    }
  }, []);

  // ── Initial load ────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const result = await deliveryService.listZones(pincode.id);
      if (!alive) return;
      if (result.success) {
        setZones(result.data);
        // Wait a tick for map to be mounted, then render
        setTimeout(() => renderZones(result.data), 0);
      } else {
        showError(result.error);
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [pincode.id, renderZones]);

  // ── Save pending polygon as new zone ────────────────────────────────────
  const handleSavePending = async () => {
    if (!pendingPolygon) return;
    if (!newZoneName.trim()) {
      showError('Please name this zone');
      return;
    }
    setSaving(true);
    const result = await deliveryService.createZone(
      pincode.id,
      newZoneName.trim(),
      pendingPolygon.geometry,
    );
    if (result.success) {
      // Remove the orange dashed temp layer
      if (pendingPolygon.layer) drawnItemsRef.current?.removeLayer(pendingPolygon.layer);
      const updated = [...zones, result.data];
      setZones(updated);
      renderZones(updated);
      setPendingPolygon(null);
      setNewZoneName('');
      showSuccess(`Zone "${result.data.name}" added`);
    } else {
      showError(result.error);
    }
    setSaving(false);
  };

  const handleCancelPending = () => {
    if (pendingPolygon?.layer) drawnItemsRef.current?.removeLayer(pendingPolygon.layer);
    setPendingPolygon(null);
    setNewZoneName('');
  };

  // ── Per-zone actions ────────────────────────────────────────────────────
  const handleToggleActive = async (zone) => {
    setBusyId(zone.id);
    const result = await deliveryService.updateZone(zone.id, { isActive: !zone.isActive });
    if (result.success) {
      const updated = zones.map((z) => (z.id === zone.id ? result.data : z));
      setZones(updated);
      renderZones(updated);
      showSuccess(`Zone "${zone.name}" ${result.data.isActive ? 'activated' : 'deactivated'}`);
    } else {
      showError(result.error);
    }
    setBusyId(null);
  };

  const handleDelete = async (zone) => {
    if (!window.confirm(`Delete zone "${zone.name}"? This cannot be undone.`)) return;
    setBusyId(zone.id);
    const result = await deliveryService.deleteZone(zone.id);
    if (result.success) {
      const updated = zones.filter((z) => z.id !== zone.id);
      setZones(updated);
      renderZones(updated);
      showSuccess(`Zone "${zone.name}" removed`);
    } else {
      showError(result.error);
    }
    setBusyId(null);
  };

  const startEditingName = (zone) => {
    setEditingId(zone.id);
    setEditName(zone.name);
  };

  const handleSaveName = async (zone) => {
    if (!editName.trim()) {
      showError('Name cannot be empty');
      return;
    }
    setBusyId(zone.id);
    const result = await deliveryService.updateZone(zone.id, { name: editName.trim() });
    if (result.success) {
      const updated = zones.map((z) => (z.id === zone.id ? result.data : z));
      setZones(updated);
      renderZones(updated);
      setEditingId(null);
      showSuccess('Name updated');
    } else {
      showError(result.error);
    }
    setBusyId(null);
  };

  const focusZone = (zone) => {
    const layer = zoneLayersRef.current.get(zone.id);
    if (layer && mapInstanceRef.current) {
      mapInstanceRef.current.fitBounds(layer.getBounds(), { padding: [40, 40] });
    }
  };

  // ── Mappls search ──────────────────────────────────────────────────────
  const handleSearchInput = (value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (value.length < 4) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      const results = await searchPlaces(value);
      setSearchResults(results);
      setIsSearching(false);
    }, 400);
  };

  const handleSelectSearchResult = async (place) => {
    let { lat, lng } = place;
    if ((!lat || isNaN(lat)) && place.placeId) {
      const coords = await resolvePlaceId(place.placeId);
      if (coords) { lat = coords.lat; lng = coords.lng; }
    }
    if (mapInstanceRef.current && lat && lng && !isNaN(lat) && !isNaN(lng)) {
      mapInstanceRef.current.flyTo([lat, lng], 16, { duration: 0.8 });
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div className="bg-white rounded-2xl shadow-xl w-[97vw] h-[94vh] max-w-[1500px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-[#548B2E]" />
            <div>
              <h2 className="text-lg font-bold text-gray-800">
                Zones for pincode {pincode.pincode}
              </h2>
              <p className="text-xs text-gray-500">
                {pincode.area || 'No area name'} · Draw polygons to mark serviceable sub-areas
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Map */}
          <div className="flex-1 relative min-h-[450px] md:min-h-0">
            <div ref={mapRef} className="absolute inset-0" />

            {/* Floating search overlay */}
            <div className="absolute top-3 left-3 z-[1000] w-[320px] max-w-[calc(100%-1.5rem)]">
              <div className="relative bg-white rounded-lg shadow-lg border border-gray-200">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchResults.length > 0) {
                      e.preventDefault();
                      handleSelectSearchResult(searchResults[0]);
                    }
                  }}
                  placeholder="Type at least 4 characters to search..."
                  className="w-full pl-9 pr-9 py-2.5 rounded-lg text-sm focus:ring-2 focus:ring-[#548B2E] focus:outline-none"
                />
                {isSearching ? (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                ) : searchQuery ? (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    title="Clear"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                ) : null}
              </div>
              {searchResults.length > 0 && (
                <div className="mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  {searchResults.map((place) => (
                    <button
                      key={place.placeId || `r-${place.lat}-${place.lng}-${place.name}`}
                      type="button"
                      onClick={() => handleSelectSearchResult(place)}
                      className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <p className="text-sm font-medium text-gray-800 truncate">{place.name}</p>
                      {place.address && (
                        <p className="text-xs text-gray-500 truncate">{place.address}</p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-[1000]">
                <Loader2 className="w-6 h-6 animate-spin text-[#548B2E]" />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-full md:w-96 border-l border-gray-100 flex flex-col overflow-hidden">
            {/* Feedback */}
            {(error || success) && (
              <div className="px-4 pt-3">
                {error && (
                  <div className="p-2 mb-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-xs">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {error}
                  </div>
                )}
                {success && (
                  <div className="p-2 mb-2 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-xs">
                    <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    {success}
                  </div>
                )}
              </div>
            )}

            {/* Pending polygon save form */}
            {pendingPolygon ? (
              <div className="px-4 py-3 bg-amber-50 border-y border-amber-200">
                <p className="text-xs font-semibold text-amber-800 mb-2">New polygon drawn</p>
                <input
                  type="text"
                  value={newZoneName}
                  onChange={(e) => setNewZoneName(e.target.value)}
                  placeholder="Zone name (e.g. Mansarovar Sector 5)"
                  className="w-full px-3 py-2 mb-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-sm"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSavePending}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#548B2E] text-white rounded-lg hover:bg-[#3f6b20] transition-colors text-sm disabled:opacity-50"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Save zone
                  </button>
                  <button
                    onClick={handleCancelPending}
                    disabled={saving}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm text-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-4 py-3 bg-blue-50 border-y border-blue-200">
                <p className="text-xs text-blue-800">
                  Use the polygon tool (top-right of map) to draw a new zone, then give it a name.
                </p>
              </div>
            )}

            {/* Zone list */}
            <div className="flex-1 overflow-y-auto p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Zones ({zones.length})
              </p>
              {zones.length === 0 && !loading ? (
                <div className="text-center py-6 text-gray-400 text-sm">
                  No zones yet. Draw the first one on the map.
                </div>
              ) : (
                <ul className="space-y-2">
                  {zones.map((z) => (
                    <li
                      key={z.id}
                      className={`p-3 rounded-lg border ${
                        z.isActive
                          ? 'border-green-200 bg-green-50/50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      {editingId === z.id ? (
                        <div className="flex gap-1">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                            autoFocus
                          />
                          <button
                            onClick={() => handleSaveName(z)}
                            disabled={busyId === z.id}
                            className="p-1 text-green-600 hover:bg-green-100 rounded"
                            title="Save"
                          >
                            {busyId === z.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-1 text-gray-500 hover:bg-gray-200 rounded"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <button
                              onClick={() => focusZone(z)}
                              className="flex-1 text-left text-sm font-semibold text-gray-800 hover:text-[#548B2E] truncate"
                              title="Center on this zone"
                            >
                              {z.name}
                            </button>
                            <span
                              className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                z.isActive ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-700'
                              }`}
                            >
                              {z.isActive ? 'ACTIVE' : 'OFF'}
                            </span>
                          </div>
                          <div className="mt-2 flex items-center gap-1">
                            <button
                              onClick={() => startEditingName(z)}
                              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-white rounded"
                              title="Rename"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleToggleActive(z)}
                              disabled={busyId === z.id}
                              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-white rounded"
                              title={z.isActive ? 'Deactivate' : 'Activate'}
                            >
                              {busyId === z.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Power className="w-3.5 h-3.5" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDelete(z)}
                              disabled={busyId === z.id}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-white rounded ml-auto"
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZoneMapEditor;
